import React from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import CommunityFootprintMap from '../CommunityFootprintMap'
import styles from './ProfileMain.module.scss'

/**
 * Shared profile main column (6 col): Community block (title = Community: location) and Activity block.
 * Used by user profile, business profile, and future profile types. Map shows when location or mapCenter is set.
 */
export default function ProfileMain({
  location,
  mapCenter,
  mapCenterLoading,
  profileType,
  mapPins,
  onOpenSlideshow,
  communityFootprintData,
  activityFeedItems,
}) {
  const { t } = useTranslation()
  const showMap = location || mapCenter || mapCenterLoading

  return (
    <div className={styles.main}>
      <section className={styles.section}>
        <div className={styles.communityTitleRow}>
          <h3 className={styles.sectionTitle}>
            {t('profiles.community', 'Community')}
            {location ? (
              <>
                : <span className={styles.communityLocation}>{location}</span>
              </>
            ) : null}
          </h3>
        </div>
        <hr className={styles.divider} />
        {showMap ? (
          <CommunityFootprintMap
            center={mapCenter}
            loading={mapCenterLoading}
            profileType={profileType}
            pins={mapPins ?? []}
            onPinClick={onOpenSlideshow}
          />
        ) : (
          <p className={styles.placeholder}>
            {t(
              'profiles.communityFootprintPlaceholder',
              'Your community footprint will appear here.',
            )}
          </p>
        )}
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>
          {t('profiles.activity', 'Activity')}
        </h3>
        {Array.isArray(activityFeedItems) && activityFeedItems.length > 0 ? (
          <ul className={styles.feedList}>
            {activityFeedItems.map((item, i) => (
              <li key={item.id ?? i} className={styles.feedItem}>
                {item.text ??
                  item.body ??
                  t('profiles.activityItem', 'Activity item')}
              </li>
            ))}
          </ul>
        ) : (
          <p className={styles.placeholder}>
            {t(
              'profiles.activityPlaceholder',
              'Your activity will appear here.',
            )}
          </p>
        )}
      </section>
    </div>
  )
}

ProfileMain.propTypes = {
  location: PropTypes.string,
  mapCenter: PropTypes.shape({
    lat: PropTypes.number.isRequired,
    lng: PropTypes.number.isRequired,
  }),
  mapCenterLoading: PropTypes.bool,
  profileType: PropTypes.oneOf(['user', 'business']),
  mapPins: PropTypes.arrayOf(
    PropTypes.shape({
      event_id: PropTypes.number.isRequired,
      event_slug: PropTypes.string,
      lat: PropTypes.number.isRequired,
      lng: PropTypes.number.isRequired,
      thumbnail_url: PropTypes.string,
      title: PropTypes.string,
      pin_type: PropTypes.string,
    }),
  ),
  onOpenSlideshow: PropTypes.func,
  communityFootprintData: PropTypes.any,
  activityFeedItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      text: PropTypes.string,
      body: PropTypes.string,
    }),
  ),
}
